import React, { useEffect, useState, useRef, Fragment } from 'react'
import { useParams } from "react-router-dom";
import { TypeLayer, TypeObjectMap } from 'Components/Map/providers/enums'
import MapObject from '../../../components/Map/providers/mapObject';

import { Loading } from 'Components';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { useWindowDimensions } from 'Core/hooks';
import { validateJsonObject } from 'Core/data/Helpers';

import { convertSecondsToTime, validateEventColor, createGreenPinIcon } from '../../../components/Map/utils';

import Map from '../../../components/Map';

import {
    mapsEtaLinkPublicGet,
    addPointers,
    drawGetDirections,
    setMapRoutesOptions,
    clearRedux
} from "Redux/actions";

import './styles.scss';

let intervalNewData;

/** SVG icon for clock - color is injected dynamically */
const buildClockSvg = (color = '#005bf8') => `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;

/** SVG icon for route/distance */
const routeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="3"/><line x1="12" y1="8" x2="12" y2="16"/><circle cx="12" cy="19" r="3"/></svg>`;

/** Default route color fallback */
const DEFAULT_ROUTE_COLOR = '#005bf8';

/** Builds the HTML content for the always-open ETA label InfoWindow */
const buildEtaLabelContent = (etaText, distanceText, vehicleLabel, routeColor) => {
    const color = routeColor || DEFAULT_ROUTE_COLOR;
    return `<div class="eta-marker-label">
        ${vehicleLabel ? `<div class="eta-marker-label__header">
            <span class="eta-marker-label__vehicle">${vehicleLabel}</span>
        </div>` : ''}
        <div class="eta-marker-label__row eta-marker-label__row--main">
            ${buildClockSvg(color)}
            <span class="eta-marker-label__time" style="color: ${color}">${etaText}</span>
        </div>
        ${distanceText ? `<div class="eta-marker-label__row">
            ${routeSvg}
            <span class="eta-marker-label__distance">${distanceText}</span>
        </div>` : ''}
    </div>`;
};

const EtaLink = (props) => {
    const { messages } = useIntl();
    const mapRef = useRef();
    const dispatch = useDispatch();

    const { code } = useParams();
    const [formData, setFormData] = useState({});
    const [drawLineRoute, setDrawLineRoute] = useState();
    const drawLineRouteRef = useRef(drawLineRoute);
    const [eta, setEta] = useState();
    const [etaDistance, setEtaDistance] = useState();
    const etaInfoWindowRef = useRef(null);

    const { width } = useWindowDimensions();

    //define the default values of the routes.... this values can be changed
    const routeProperties = {
        optimizeWaypoints: true,
        avoidTolls: false,
        avoidHighways: false
    }

    //Get the information at the points saved 
    const {
        pointers,
        directions
    } = useSelector((state) => state.realtimeMapRedux);

    const response = useSelector(state => state.etaLinkRedux.response);
    const error = useSelector(state => state.etaLinkRedux.error);
    const loading = useSelector(state => state.etaLinkRedux.loading);

    useEffect(() => {

        //temporally define the points into the view
        dispatch(mapsEtaLinkPublicGet(code, true))

        //create interval to get information from API
        if (!intervalNewData) {
            intervalNewData = setInterval((codeParam) => {
                dispatch(clearRedux({ response: null }))
                dispatch(mapsEtaLinkPublicGet(codeParam))
            }, 15000, code);
        }
    }, [])

    useEffect(() => {
        if (response) {
            if (response?.entityName == "Unit") {

                const startPoint = {
                    latitude: response?.unit?.lastReading?.latitude,
                    longitude: response?.unit?.lastReading?.longitude,
                    driverName: response?.unit?.driverName,
                    label: response?.unit?.label,
                    eventCode: response?.unit?.lastReading?.eventCode,
                    eventName: response?.unit?.lastReading?.eventName,
                    heading: response?.unit?.lastReading?.heading,
                    id: response?.unitId
                };

                const endPoint = {
                    latitude: response?.latitude,
                    longitude: response?.longitude,
                    label: response?.address
                };
                setFormData({ entityName: response?.entityName, startPoint, endPoint, isKph: response.isKph });

            }

            if (response?.entityName == "Route") {

                const vehicle = {
                    latitude: response?.unit?.lastReading?.latitude,
                    longitude: response?.unit?.lastReading?.longitude,
                    driverName: response?.unit?.driverName,
                    label: response?.unit?.label,
                    eventCode: response?.unit?.lastReading?.eventCode,
                    eventName: response?.unit?.lastReading?.eventName,
                    heading: response?.unit?.lastReading?.heading,
                    id: response?.unitId
                };

                setFormData({
                    entityName: response?.entityName,
                    routePoints: response?.routePoints,
                    vehicle,
                    isKph: response.isKph,
                    drawLineRoute: response?.drawLineRoute,
                    colorRoute: response?.color ? (response.color.includes('#') ? response.color : `#${response.color}`) : null
                });
            }
        }
    }, [response]);


    useEffect(() => {
        if (error == "NO_DATA") {
            //end the get information
            clearInterval(intervalNewData);
        }
        if (error == "LOAD_FAILED") {
            if (!intervalNewData) {
                dispatch(mapsEtaLinkPublicGet(code))
                intervalNewData = setInterval((codeParam) => {
                    dispatch(mapsEtaLinkPublicGet(codeParam))
                }, 15000, code);
            }
        }
    }, [error]);

    useEffect(() => {
        if (
            formData?.startPoint?.latitude &&
            formData?.startPoint?.longitude &&
            formData?.endPoint?.latitude &&
            formData?.endPoint?.longitude
        ) {
            if (formData?.entityName == "Unit") {
                createRouteOnView(formData?.startPoint, formData?.endPoint);
            }
        }
    }, [
        formData?.startPoint?.latitude,
        formData?.startPoint?.longitude,
        formData?.endPoint?.latitude,
        formData?.endPoint?.longitude
    ]);

    useEffect(() => {
        if (formData?.vehicle?.latitude && formData?.vehicle?.longitude) {
            if (formData?.entityName == "Route") {
                displayElementsOnMapEta(formData);
            }
        }
    }, formData?.vehicle)

    // Update ETA InfoWindow content when eta changes
    useEffect(() => {
        if (etaInfoWindowRef.current && eta) {
            const vehicleLabel = formData?.vehicle?.label || formData?.startPoint?.label || '';
            const routeColor = formData?.colorRoute || DEFAULT_ROUTE_COLOR;
            etaInfoWindowRef.current.setContent(buildEtaLabelContent(eta, etaDistance, vehicleLabel, routeColor));
        }
    }, [eta, etaDistance]);

    // Green destination pin icon for ETA link markers
    const greenPinIcon = createGreenPinIcon();

    const createRouteOnView = (startPoint, endPoint) => {
        // Close existing ETA InfoWindow before removing markers
        if (etaInfoWindowRef.current) {
            etaInfoWindowRef.current.close();
            etaInfoWindowRef.current = null;
        }

        let getOpenInfoWindow = mapRef.current?.getOpenInfoWindow()
        mapRef.current.hideMarkerModal();
        mapRef?.current?.removeLayer(TypeLayer.Units);
        mapRef?.current?.deleteRouteInMap();
        dispatch(drawGetDirections(false));
        dispatch(addPointers([]));

        mapRef?.current?.removeMapObject(startPoint?.id, TypeObjectMap.Marker);
        mapRef?.current?.removeMapObject('eta_destination', TypeObjectMap.Marker);

        const tempPointers = [
            {
                id: startPoint?.id,
                lat: parseFloat(startPoint?.latitude || 0),
                lng: parseFloat(startPoint?.longitude || 0),
                label: startPoint?.driverName && startPoint?.driverName != "true" && startPoint?.driverName != "null"
                    ?
                    `${startPoint?.label} (${startPoint?.driverName})` : startPoint?.label,
                heading: startPoint?.heading || 0,
                color: '000',
                eventName: startPoint?.eventName,
                draggable: false,
                updatePointers: true,
                hideMarker: true,
                isUnit: true
            },
            {
                id: '2',
                lat: parseFloat(endPoint?.latitude || 0),
                lng: parseFloat(endPoint?.longitude || 0),
                label: endPoint.label || '',
                heading: 0,
                color: '000',
                eventName: 'no event',
                draggable: false,
                updatePointers: true,
                hideMarker: true
            }
        ];

        //save pointers and shows into map
        dispatch(addPointers([...tempPointers]));
        dispatch(setMapRoutesOptions(routeProperties));

        // Add green destination pin marker
        mapRef?.current?.addMapObject(new MapObject().buildMarker({
            id: 'eta_destination',
            position: {
                lat: parseFloat(endPoint?.latitude || 0),
                lng: parseFloat(endPoint?.longitude || 0)
            },
            layerName: TypeLayer.Pointers,
            label: endPoint.label || '',
            heading: 0,
            color: '#34A853',
            draggable: false,
            hideMarker: false,
            noClickEvent: true,
            icon: greenPinIcon
        }));

        // Calculate ETA and show InfoWindow for Unit entity
        const originPos = {
            lat: parseFloat(startPoint?.latitude || 0),
            lng: parseFloat(startPoint?.longitude || 0)
        };
        const destPos = {
            lat: parseFloat(endPoint?.latitude || 0),
            lng: parseFloat(endPoint?.longitude || 0)
        };
        getEtaFromRoute(originPos, destPos);
    }

    const displayElementsOnMapEta = (data) => {
        const { entityName, routePoints, vehicle, isKph, drawLineRoute : paramDrawLineRoute } = data
        if(!mapRef?.current) return;
        
        dispatch(addPointers([]));

        let points = validateJsonObject(routePoints);
        points = validateJsonObject(points);

        if(!drawLineRouteRef.current && paramDrawLineRoute){
            setDrawLineRoute(paramDrawLineRoute);
            drawLineRouteRef.current = paramDrawLineRoute;
        }

        // Close existing ETA InfoWindow before removing markers
        if (etaInfoWindowRef.current) {
            etaInfoWindowRef.current.close();
            etaInfoWindowRef.current = null;
        }

        let getOpenInfoWindow = mapRef.current?.getOpenInfoWindow()
        mapRef?.current?.hideMarkerModal();
        mapRef?.current?.removeLayer(TypeLayer.Units);
        mapRef?.current?.removeMapObject(vehicle?.id, TypeObjectMap.Marker);
        mapRef?.current?.removeMapObject('eta_destination', TypeObjectMap.Marker);

        //set the vehicle value
        const lastPoint = points[points.length - 1];
        const tempPointers = [{
            id: 1,
            lat: parseFloat(vehicle?.latitude || 0),
            lng: parseFloat(vehicle?.longitude || 0),
            label: vehicle?.driverName && vehicle?.driverName != "true" && vehicle?.driverName != "null"
                ?
                `${vehicle?.label} (${vehicle?.driverName})` : vehicle?.label,
            heading: vehicle?.heading || 0,
            color: '000',
            eventName: vehicle?.eventName,
            draggable: false,
            updatePointers: true,
            hideMarker: true,
            isUnit: true
        },{
            id: '2',
            lat: parseFloat(lastPoint?.lat || 0),
            lng: parseFloat(lastPoint?.lon || 0),
            label: lastPoint?.name || '',
            heading: 0,
            color: '000',
            eventName: 'no event',
            draggable: false,
            updatePointers: true,
            hideMarker: true
        }]

        const position = {
            lat: parseFloat(vehicle?.latitude || 0),
            lng: parseFloat(vehicle?.longitude || 0)
        };

        mapRef?.current?.addMapObject(new MapObject().buildMarker({
            id: vehicle?.id,
            deviceId: vehicle?.id,
            position,
            layerName: TypeLayer.Units,
            label: vehicle?.label,
            heading: vehicle?.heading,
            color: validateEventColor(vehicle),
            eventCode: vehicle?.eventCode,
            eventName: vehicle?.eventName,
            draggable: false,
            updatePointers: false,
            hasVideo: false,
            hideMarker: false,
            value: '',
            hasChild: false,
            isRouteTracker: false,
            trail:  null,
            trailTimestamp: 0,
            noClickEvent: false
        }));


        points?.map((element, index) => {
            tempPointers.push({
                ...element,
                id: index + 4,
                label: element?.name || '',
                latitude: element?.lat,
                longitude: element?.lon,
                lng: element?.lon,
                layerName: TypeLayer?.FSMRoute,
                eventName: 'Stop',
                sequence: index,
                typePoint: 'Stop',
                type: 'RoutePoint',
                noClickEvent: false,
                value: index
            })
        });
        dispatch(addPointers([...tempPointers]));

        // Add green destination pin marker
        mapRef?.current?.addMapObject(new MapObject().buildMarker({
            id: 'eta_destination',
            position: {
                lat: parseFloat(lastPoint?.lat || 0),
                lng: parseFloat(lastPoint?.lon || 0)
            },
            layerName: TypeLayer.Pointers,
            label: lastPoint?.name || '',
            heading: 0,
            color: '#34A853',
            draggable: false,
            hideMarker: false,
            noClickEvent: true,
            icon: greenPinIcon
        }));

        if(drawLineRoute || paramDrawLineRoute){
            mapRef?.current?.removeMapObject(`temporaldrawRoute_polyline_${1}`, TypeObjectMap.Polyline);
            mapRef?.current?.addMapObject(new MapObject().buildPolyLine({
                id: `temporaldrawRoute_polyline_${1}`,
                coordinates: drawLineRoute || paramDrawLineRoute,
                layerName: TypeLayer.FSMRoute,
                heading: 0,
                color: formData?.colorRoute || DEFAULT_ROUTE_COLOR,
                hideDrawDirection: true,
                clickable: false,
                strokeOpacity: 0.7,
                strokeWeight: 5,
            }), true, true);
        }

        
        getEtaFromRoute(position, { lat: parseFloat(lastPoint?.lat || 0), lng: parseFloat(lastPoint?.lon || 0) }, paramDrawLineRoute);

    }

    /********************************************************
     * recreate the route in map when have 2 points
     ********************************************************/
    useEffect(() => {
        if (pointers && pointers.length > 1 && response?.entityName != "Route") {
            //delete the old rounte on map
            mapRef?.current?.deleteRouteInMap()
            //create the new pointers of the view
            dispatch(drawGetDirections(true))
        }
    }, [pointers])

    const getEtaFromRoute = (origin, destination, paramDrawLineRoute) => {
        const directionsRenderer = new google.maps.DirectionsRenderer();
        const directionsService = new google.maps.DirectionsService();
        
        directionsRenderer.setOptions({
            suppressMarkers: true,
            preserveViewport: true,
            polylineOptions: {
              strokeColor: formData?.colorRoute || DEFAULT_ROUTE_COLOR,
              strokeOpacity: 0.7,
              strokeWeight: 5
            }
        });
        // Make the petition to directions.
        const request = {
          origin: origin,
          destination: destination,
          travelMode: google.maps.TravelMode.DRIVING
        };

        // Get the petitions to directions
        directionsService.route(request, (result, status) => {
            if (status === google.maps.DirectionsStatus.OK) {
                // Get the eta for the results
                const valueTime = convertSecondsToTime(result.routes[0].legs[0].duration.value || 0)
                
                const timeFormatToShow = `
                    ${valueTime?.days > 0 ? `${valueTime?.days} ${valueTime?.days == 1 ? messages['Day'] : messages['Days']}` : ''} 
                    ${valueTime?.hour > 0 ? `${valueTime?.hour} ${valueTime?.hour == 1 ? messages['hour'] : messages['hours']}` : ''} 
                    ${valueTime?.min > 0 ? `${valueTime?.min} ${valueTime?.min == 1 ? messages['min'] : messages['mins']}` : ''} `
                let cleanedStr = timeFormatToShow.replace(/\s+/g, ' ').trim();
                setEta(cleanedStr);

                // Extract distance and convert based on isKph setting
                const leg = result.routes[0].legs[0];
                const distanceMeters = leg.distance?.value || 0;
                const isKph = formData?.isKph || false;
                let distText = '';
                if (isKph) {
                    const km = (distanceMeters / 1000).toFixed(1);
                    distText = `${km} ${messages['kilometers'] || 'km'}`;
                } else {
                    const mi = (distanceMeters / 1609.344).toFixed(1);
                    distText = `${mi} ${messages['miles'] || 'mi'}`;
                }
                setEtaDistance(distText);

                // Store in Redux so InfoBubble can display ETA info
                dispatch(clearRedux("MAPS_ETA_LINK", { eta: cleanedStr, etaDistance: distText }));

                // Vehicle label and route color for the InfoWindow
                const vehicleLabel = formData?.vehicle?.label || formData?.startPoint?.label || '';
                const routeColor = formData?.colorRoute || DEFAULT_ROUTE_COLOR;

                // Open/update the ETA label InfoWindow at the vehicle position
                if (!etaInfoWindowRef.current) {
                    etaInfoWindowRef.current = new google.maps.InfoWindow({
                        content: buildEtaLabelContent(cleanedStr, distText, vehicleLabel, routeColor),
                        disableAutoPan: true,
                        pixelOffset: new google.maps.Size(0, -40)
                    });
                    etaInfoWindowRef.current.setPosition(origin);
                    etaInfoWindowRef.current.open({
                        map: mapRef?.current?.mapItem,
                        shouldFocus: false
                    });
                } else {
                    etaInfoWindowRef.current.setContent(buildEtaLabelContent(cleanedStr, distText, vehicleLabel, routeColor));
                    etaInfoWindowRef.current.setPosition(origin);
                    if (!etaInfoWindowRef.current.getMap()) {
                        etaInfoWindowRef.current.open({
                            map: mapRef?.current?.mapItem,
                            shouldFocus: false
                        });
                    }
                }

                if(!drawLineRouteRef.current && !paramDrawLineRoute){
                    directionsRenderer?.setMap(null);
                    directionsRenderer?.setDirections(result);
                    directionsRenderer?.setMap(mapRef?.current?.mapItem);
                }
                
            }
        });
    }

    let messagesToMap = new Array();
    if (messages) {
        messagesToMap['kilometers'] = messages['kilometers']
        messagesToMap['miles'] = messages['miles']
        messagesToMap['Day'] = messages['Day']
        messagesToMap['Days'] = messages['Days']
        messagesToMap['hour'] = messages['hour']
        messagesToMap['hours'] = messages['hours']
        messagesToMap['min'] = messages['min']
        messagesToMap['mins'] = messages['mins']
    }

    return (
        <div
            style={{
                width: '100%',
                height: '100%'
            }}
        >
            {
                loading ?
                    <Loading full /> :
                    <Fragment>
                        {
                            error == "NO_DATA" ?
                                <div className='eta-error-container'>
                                    <div className='eta-error-message'>
                                        Error 400: Content not available
                                    </div>
                                </div> :
                                <Map
                                    ref={mapRef}
                                    pointers={pointers || []}
                                    directions={directions}
                                    noRealtime={true}
                                    isEtaLink={true}
                                    hiddenButton
                                    hiddenLegends={true}
                                    hiddenTraffic={true}
                                    hiddenLayerToggle={true}
                                    hiddenSearchMap
                                    height="100vh"
                                    showEtaLink={true}
                                    isMobile={width <= 767}
                                    messages={messagesToMap}
                                    isKph={formData?.isKph || false}
                                    showInfoWindow
                                    noCenterPoints={false}
                                    etaLabel={eta ? `${messages['etalink_eta']}: ${eta}` : null}
                                />
                        }
                    </Fragment>
            }
        </div>
    )

}

export default EtaLink;